<template>
  <app-page wide title="BGC Files">
    <v-flex shrink slot="actions">
      <v-menu
        ref="modal"
        v-model="dateModal"
        :close-on-content-click="false"
        :nudge-right="40"
        lazy
        transition="scale-transition"
        offset-y
        full-width
        max-width="290px"
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            color="green"
            v-model="dateFormatted"
            readonly
            prepend-icon="event"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="date"
          color="green"
          no-title
          @input="dateModal = false"
        ></v-date-picker>
      </v-menu>
    </v-flex>

    <v-layout>
      <v-flex xs12>
        <errors-list :errorMessage="errorMessage" />

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :rows-per-page-items="rowsPerPageItems"
          :total-items="pagination.totalItems"
          :pagination.sync="pagination"
        >
          <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
          <template slot="items" slot-scope="props">
            <tr>
              <grid-cell
                :header="header"
                :row="props.item"
                v-for="(header, index) in headers"
                :key="index"
              ></grid-cell>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </app-page>
</template>

<script>
import moment from 'moment';
import GridCell from '../../components/GridCell';
import { serializeQuery, defaultDateTimeFormatter } from '../../utils';
import { getErrorMessage } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';

const BgcFileType = {
  toBgc: 'To BGC',
  fromBgc: 'From BGC',
};

export default {
  components: { GridCell, ErrorsList },
  data() {
    return {
      params: {
        from: moment()
          .startOf('month')
          .startOf('day')
          .format(),
      },
      date: null,
      items: [],

      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'createdAt',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
      headers: [
        {
          text: this.$t('@bgcFilesList/directionColumnHeader'),
          align: 'left',
          value: 'direction',
          format: (v) => BgcFileType[v] || '',
        },
        { text: this.$t('@bgcFilesList/NameColumnHeader'), value: 'name' },
        {
          text: this.$t('@bgcFilesList/createdAtColumnHeader'),
          value: 'createdAt',
          format: defaultDateTimeFormatter,
        },
        {
          text: this.$t('@bgcFilesList/actionsColumnHeader'),
          width: 40,
          sortable: false,
          actions: (row) => [{ icon: 'cloud_download', href: `/api/bgc/datasets/${row.id}` }],
        },
      ],

      dateModal: false,
      loading: false,
      errorMessage: '',
    };
  },

  watch: {
    pagination() {
      this.fetchData();
    },

    date(val) {
      this.params.from = moment(val)
        .startOf('day')
        .format();
      this.fetchData();
    },
  },

  computed: {
    dateFormatted() {
      return moment(this.params.from).format('DD/MM/YYYY');
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        ...this.params,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy = `dataset.${this.pagination.sortBy}`;
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
      } else {
        delete query.sortBy;
        delete query.sortOrder;
      }

      if (!query.from) {
        query.from = moment()
          .startOf('month')
          .startOf('day')
          .format();
      }
      return query;
    },
  },

  methods: {
    async updateRoute(query) {
      await this.$router.replace({
        name: 'bgcList',
        query: Object.assign({}, this.$route.query, query),
      });
    },

    async fetchData() {
      try {
        this.loading = true;

        await this.updateRoute(this.query);
        const qs = serializeQuery(this.query);
        const {
          data: {
            data,
            meta: { total },
          },
        } = await this.$http.get(`/api/bgc/datasets?${qs}`);
        this.pagination.totalItems = total;
        this.items = data;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.params = {
      ...this.params,
      ...this.$router.currentRoute.query,
    };
  },

  beforeRouteUpdate(to, from, next) {
    this.params = { ...this.params, ...to.query };
    next();
  },
};
</script>
